import { DownOutlined } from '@ant-design/icons';
import { Dropdown } from '@framework/ui/components/dropdown/Dropdown.tsx';
import { Button, type ButtonProps } from 'antd';
import { type ItemType } from 'antd/es/menu/interface';
import React, { useMemo } from 'react';
import { type AlboConfigurazioneTipoDTO } from '../../configuration/api/schemas/alboConfigurazioneTipoDTO.ts';

/** Props per il dropdown condiviso di export albi (BO/FO). */
interface IRegisterRecordExportDropDownProps {
  readonly registerRecordType: AlboConfigurazioneTipoDTO | null;
  readonly query: { pagina?: number; limite?: number; [key: string]: unknown };
  readonly csvExportFlagField: 'flExportCsvBo' | 'flExportCsvFo';
  readonly pdfExportFlagField: 'flExportPdfBo' | 'flExportPdfFo';
  readonly ExportButton: React.ComponentType<{
    type: 'csv' | 'pdf';
    query: any;
    registerRecordTypeId: string;
    registerRecordTypeName: string;
    buttonType?: ButtonProps['type'];
    asMenuItem?: boolean;
  }>;
}

/**
 * Dropdown condiviso per l'export di record albo.
 * Mostra le voci CSV e/o PDF in base ai flag di configurazione
 * del tipo albo. Se un solo formato è abilitato, renderizza
 * direttamente il bottone senza dropdown.
 */
export function RegisterRecordExportDropDown(
  props: IRegisterRecordExportDropDownProps,
) {
  const {
    registerRecordType,
    query,
    csvExportFlagField,
    pdfExportFlagField,
    ExportButton,
  } = props;

  const isCsvExportEnabled = registerRecordType?.[csvExportFlagField] ?? false;
  const isPdfExportEnabled = registerRecordType?.[pdfExportFlagField] ?? false;
  const idTipoAlbo = registerRecordType?.id ?? '';
  const denominazione = registerRecordType?.denominazione ?? 'Albo';

  const menuItems = useMemo(() => {
    const items: ItemType[] = [];
    if (isCsvExportEnabled) {
      items.push({
        style: { padding: '0' },
        label: (
          <ExportButton
            key={idTipoAlbo}
            type="csv"
            query={query}
            registerRecordTypeId={idTipoAlbo}
            registerRecordTypeName={denominazione}
            asMenuItem
          />
        ),
        key: 'csv',
      });
    }
    if (isPdfExportEnabled) {
      items.push({
        style: { padding: '0' },
        label: (
          <ExportButton
            key={idTipoAlbo}
            type="pdf"
            query={query}
            registerRecordTypeId={idTipoAlbo}
            registerRecordTypeName={denominazione}
            asMenuItem
          />
        ),
        key: 'pdf',
      });
    }
    return items;
  }, [
    isCsvExportEnabled,
    isPdfExportEnabled,
    query,
    idTipoAlbo,
    denominazione,
    ExportButton,
  ]);

  if (menuItems.length === 0) {
    return null;
  }

  if (menuItems.length === 1) {
    return React.cloneElement(
      (menuItems[0] as { label: React.JSX.Element }).label,
      { buttonType: 'default', asMenuItem: false },
    );
  }

  return (
    <Dropdown menu={{ items: menuItems }}>
      <Button>
        Esporta <DownOutlined />
      </Button>
    </Dropdown>
  );
}
